'use client';

import AppImage from '@/app/components/ui/AppImage';
import { AppointmentBookingTrigger } from '@/app/components/appointments/AppointmentBookingTrigger';
import { CONSULTATION_SERVICE_SLUG } from '@/lib/constants/booking';
import { GET_SERVICES_WITH_CATEGORY } from '@/lib/graphql';
import Header from '../components/common/Header';
import Icon from '@/app/components/ui/AppIcon';
import Link from 'next/link';
import { NetworkStatus } from '@apollo/client';
import PageLayout from '@/app/components/common/PageLayout';
import { Service } from '@/lib/types/service.types';
import { Skeleton } from '@/app/components/ui/skeleton';
import { motion } from 'framer-motion';
import { useQuery } from '@apollo/client/react';
import { useSearchParams } from 'next/navigation';
import { Suspense } from 'react';
import { stripHtml } from '@/lib/utils/strip-html';

interface ServicesQueryMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface ServicesQueryData {
  services: {
    data: Service[];
    meta: ServicesQueryMeta;
  };
}

interface ServicesQueryVariables {
  pagination: {
    page: number
    limit: number
  }
  filters?: {
    categorySlug?: string
    featured?: boolean
  }
}

const PAGE_SIZE = 10;

function ServicesPageContent() {
  const searchParams = useSearchParams();
  const categorySlug = searchParams.get('category') || undefined;

  const {
    data: servicesData,
    fetchMore,
    networkStatus,
  } = useQuery<ServicesQueryData, ServicesQueryVariables>(GET_SERVICES_WITH_CATEGORY, {
    variables: {
      pagination: { page: 1, limit: PAGE_SIZE },
      filters: categorySlug ? { categorySlug } : {},
    },
    notifyOnNetworkStatusChange: true,
  });

  const featuredServices = servicesData?.services?.data ?? [];

  const servicesMeta = servicesData?.services?.meta;
  const hasNextPage = Boolean(servicesMeta?.hasNext);
  const isLoadingMore = networkStatus === NetworkStatus.fetchMore;
  const isInitialLoading = networkStatus === NetworkStatus.loading && featuredServices.length === 0;

  const handleLoadMore = async () => {
    if (!servicesMeta || !hasNextPage || isLoadingMore) return;

    await fetchMore({
      variables: {
        pagination: { page: servicesMeta.page + 1, limit: PAGE_SIZE },
        filters: categorySlug ? { categorySlug } : {},
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.services?.data?.length) {
          return previousResult;
        }

        const existingIds = new Set(previousResult.services.data.map((service) => service.id));
        const appendedServices = fetchMoreResult.services.data.filter(
          (service) => !existingIds.has(service.id)
        );

        return {
          services: {
            ...fetchMoreResult.services,
            data: [...previousResult.services.data, ...appendedServices],
          },
        };
      },
    });
  };

  return (
    <PageLayout>
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <Header />
          <div className='relative z-10'>
            <AppImage
              src="/resource/bg_image.jpg"
              alt="Hair styling hero image"
              className="w-full h-full object-cover"
              priority
            />
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-transparent" />
          </div>
        </div>
        <div className="container mx-auto px-6 relative z-10 py-20 mt-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mb-6">
              Our Services
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Expert hair care tailored to your texture, lifestyle, and goals. From protective styles and treatments to everyday maintenance, we offer a full range of services to keep your hair healthy and beautiful.
            </p>
            <AppointmentBookingTrigger serviceSlugs={[CONSULTATION_SERVICE_SLUG]}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 py-4 rounded-xl font-semibold text-lg shadow-lg shadow-primary/30 transition-all"
              >
                Schedule a Consultation
              </motion.button>
            </AppointmentBookingTrigger>          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-32 bg-background">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-3xl mx-auto mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              All Our Services
            </h2>
            <p className="text-lg text-muted-foreground">
              Explore our full range of hair services designed for every texture, style preference, and hair goal
            </p>
          </motion.div>

          {isInitialLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {Array.from({ length: 6 }).map((_, index) => (
                <div key={`initial-skeleton-${index}`} className="bg-card rounded-3xl overflow-hidden shadow-lg border border-border h-full flex flex-col">
                  <Skeleton className="h-64 w-full rounded-none" />
                  <div className="p-8 flex-1 flex flex-col">
                    <Skeleton className="h-5 w-3/4 mb-4" />
                    <div className="space-y-3 mb-6 flex-1">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-5/6" />
                      <Skeleton className="h-4 w-4/6" />
                    </div>
                    <Skeleton className="h-4 w-1/3 mb-3" />
                    <Skeleton className="h-6 w-1/4" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
                {featuredServices.map((service, index) => (
                  <motion.div
                    key={service.id}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    whileHover={{ y: -8 }}
                    className="group"
                  >
                    <Link href={`/services/${service.slug}`}>
                      <div className="bg-card rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 border border-border h-full flex flex-col">
                        {/* Image */}
                        <div className="relative h-64 overflow-hidden">
                          <AppImage
                            src={service.image?.url || '/resource/placeholder.png'}
                            alt={service.service_name}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                            fill
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                          <div className="absolute bottom-6 left-6 right-6">
                            <h3 className="text-3xl font-bold text-white mb-2">
                              {service.service_name}
                            </h3>
                          </div>
                        </div>

                        {/* Content */}
                        <div className="p-8 flex-1 flex flex-col">
                          <p className="text-muted-foreground mb-6 leading-relaxed flex-1">
                            {service.service_description
                              ? stripHtml(service.service_description)
                              : service.service_name}
                          </p>

                          {/* Features */}
                          <div className="mb-6">
                            <h4 className="text-sm font-semibold text-foreground mb-3 uppercase tracking-wider">
                              Price
                            </h4>
                            <p className="text-lg text-foreground font-bold">
                              ₦{service.service_price.toLocaleString()}
                            </p>
                            {/* <ul className="space-y-2">
                              {service.features.slice(0, 3).map((feature, featureIndex) => (
                                <li key={featureIndex} className="flex items-center gap-2 text-sm text-muted-foreground">
                                  <Icon name="CheckCircleIcon" size={16} className="text-primary flex-shrink-0" />
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul> */}
                          </div>

                          {/* CTA */}
                          <div className="flex items-center gap-2 text-primary font-semibold group-hover:gap-3 transition-all mt-auto">
                            <span>Learn More</span>
                            <Icon name="ArrowRightIcon" size={20} className="group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </div>

              {isLoadingMore && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto mt-8">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={`load-more-skeleton-${index}`} className="bg-card rounded-3xl overflow-hidden shadow-lg border border-border h-full flex flex-col">
                      <Skeleton className="h-56 w-full rounded-none" />
                      <div className="p-6">
                        <Skeleton className="h-5 w-2/3 mb-4" />
                        <div className="space-y-3">
                          <Skeleton className="h-4 w-full" />
                          <Skeleton className="h-4 w-5/6" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {hasNextPage && (
                <div className="mt-12 flex justify-center">
                  <motion.button
                    whileHover={{ scale: isLoadingMore ? 1 : 1.05 }}
                    whileTap={{ scale: isLoadingMore ? 1 : 0.95 }}
                    onClick={handleLoadMore}
                    disabled={isLoadingMore}
                    className="bg-primary hover:bg-primary/90 disabled:bg-primary/70 disabled:cursor-not-allowed text-primary-foreground px-8 py-4 rounded-xl font-semibold shadow-lg shadow-primary/30 transition-all"
                  >
                    {isLoadingMore ? 'Loading...' : 'Learn More'}
                  </motion.button>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 bg-secondary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-6">
                Why Choose Mevoly Hair?
              </h2>
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
                We combine expert technique, healthy hair care practices, and personal attention to deliver styles you will love and feel confident wearing.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: 'ScissorsIcon',
                  title: 'Skilled Stylists',
                  description: 'Our experienced stylists understand natural textures and create looks that match your personality, lifestyle, and goals.'
                },
                {
                  icon: 'SparklesIcon',
                  title: 'Healthy Hair First',
                  description: 'From scalp care to protective styling, every service is designed to keep your hair strong, nourished, and easy to maintain.'
                },
                {
                  icon: 'HeartIcon',
                  title: 'Personalized Experience',
                  description: 'We take time to listen, recommend what works best for you, and make every visit comfortable from start to finish.'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-card rounded-3xl p-8 text-center shadow-lg border border-border"
                >
                  <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                    <Icon name={item.icon as string} size={32} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-4">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-primary-foreground">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl font-bold mb-6">Ready for Your Next Look?</h2>
            <p className="text-lg mb-8 opacity-90">
              Book your appointment today and let our team help you achieve a fresh, confident style you can wear every day.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <AppointmentBookingTrigger serviceSlugs={[CONSULTATION_SERVICE_SLUG]}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-white text-primary hover:bg-white/90 px-8 py-4 rounded-xl font-semibold shadow-lg transition-all"
                >
                  Book Your Appointment
                </motion.button>
              </AppointmentBookingTrigger>
              <Link
                href="/faq"
                className="text-white hover:underline px-8 py-4 font-semibold"
              >
                View FAQ
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </PageLayout>
  );
}

export default function ServicesPage() {
  return (
    <Suspense
      fallback={
        <PageLayout>
          <div className="flex min-h-[40vh] items-center justify-center">
            <Skeleton className="h-8 w-48" />
          </div>
        </PageLayout>
      }
    >
      <ServicesPageContent />
    </Suspense>
  );
}

